import {
	lazyFindChildElement,
	lazyFindClosestElement,
	lazyFindElement,
	lazyFindParentElement,
	useActions,
	useCedarStore,
	useOnboarding,
	useTriggers,
} from 'cedar-ui';
import { useState, useEffect } from 'react';
import { createWalkthroughSteps as createFullWalkthroughSteps } from './WalkthroughShortened';
import { createPreSurveySteps } from './WalkthroughShortened';

const customSpecialties = ["LACN", "LACN Gastro", "lacn_gyn_onc", "self_serve", 'ironwood medonc', 'ironwood gyn', 'ironwood radonc', 'tenonc medonc'];


export const isCustomSpecialty = (specialty) => {
	return customSpecialties.includes(specialty);
};

// Get specialty example text based on specialty
export const getSpecialtyExamples = (specialty) => {
	const specialtyExamples = {
		LACN: {
			patientIntro:
				"I'm seeing Maria Garcia who was referred by Dr. Kurzman for anemia",
			physicalFindings:
				'Her red blood cells are low, which can cause fatigue and sleepiness',
			followUpPlans:
				"I'd like to order you a CBC CMP and LDH and I'll see you back in three weeks",
			additionalRecording:
				"After reviewing the labs, the CBC showed hemoglobin of 9.2, indicating moderate anemia. Let's order iron studies and start her on an iron supplement.",
		},
		lacn_gyn_onc: {
			patientIntro:
				"I'm evaluating Jane Smith for a pelvic mass approximately 16 centimeters in size",
			physicalFindings:
				'There appears to be a mass in the abdominal area, approximately 16 centimeters',
			followUpPlans:
				"We'll need to schedule surgery to remove the mass and send it to pathology",
			additionalRecording:
				'After reviewing the CT scan, the mass appears to be confined to the uterus with no visible lymph node involvement. We should still plan for a total hysterectomy with potential lymph node sampling.',
		},
		self_serve: {
			patientIntro:
				"I'm evaluating John Doe for persistent abdominal pain that has been ongoing for three weeks",
			physicalFindings:
				'There is tenderness in the lower right quadrant with mild guarding, and the patient reports pain level of 6 out of 10',
			followUpPlans:
				"I'd like to order an abdominal ultrasound and some blood work, and we'll schedule a follow-up in two weeks to review the results",
			additionalRecording:
				'After reviewing the ultrasound results, there appears to be mild inflammation but no mass or obstruction. The blood work shows slightly elevated WBC count, suggesting we should start on a course of antibiotics and monitor for improvement.',
		},
	};

	// Use default specialty if the provided one isn't found
	return specialtyExamples[specialty] || specialtyExamples['self_serve'];
};

// Shared step collections - defined as functions that take specialty as parameter
const createIntroductionSteps = (specialty) => {
	const baseMessages = [
		'Hi, welcome to Knowtex. If you have a few minutes, I\'ll show you around the platform.',
		'If you need to stop or restart the tutorial, you can find me at the bottom right of the screen.',
	];
	
	// Only include EHR integration message for custom specialties
	if (isCustomSpecialty(specialty)) {
		baseMessages.push("Let's start with integrating your EHR system.");
	}
	
	return baseMessages.map((message, index) => ({
		id: `welcome-message-${index}`,
		type: 'DIALOGUE',
		text: message,
		blocking: true,
	}));
};

const createIntegrationSetupSteps = (specialty) => {
	if (!isCustomSpecialty(specialty)) return [];

	return [
		{
			id: 'settings-redirect',
			type: 'VIRTUAL_CLICK',
			endPosition: lazyFindElement(
				{ role: 'button', name: 'Settings' },
				{ x: 800, y: 500 }
			),
			tooltipText: 'Navigate to the settings page.',
			blocking: true,
		},
		{
			id: 'integration-button',
			type: 'VIRTUAL_CLICK',
			endPosition: lazyFindElement(
				{ role: 'button', name: 'Set Up Integration' },
				{ x: 800, y: 500 }
			),
			tooltipText:
				'Click here to integrate your EHR system, which allows Knowtex to automatically sync your notes',
			blocking: true,
		},
		{
			id: 'integration-dialogue',
			type: 'DIALOGUE',
			text: 'This pop up will ask you to log in to Knowtex, then log in to your EHR system to link them!',
			blocking: true,
		},
		{
			id: 'checkCobaltIdle',
			type: 'IDLE',
			blocking: true,
			advanceFunction: (nextAction) => {
				// Set up a MutationObserver to monitor the DOM for element removal
				const targetNode = document.body;

				// Create a function to check if our test element exists
				const checkForElement = () => {
					const testElement = document.querySelector(
						"iframe[src^='https://link.usecobalt.com/index.html']"
					);
					return !!testElement;
				};

				// If the element already doesn't exist, advance immediately
				if (!checkForElement()) {
					nextAction();
					return;
				}

				// Otherwise, set up an observer to watch for it to be removed
				const observer = new MutationObserver(() => {
					// After each mutation, check if our element is gone
					if (!checkForElement()) {
						// Element has been removed, advance to next action
						nextAction();
						// Disconnect the observer since we're done with it
						observer.disconnect();
					}
				});

				// Start observing with configuration to watch for child list changes
				observer.observe(targetNode, {
					childList: true,
					subtree: true,
				});

				// Return a cleanup function (though in this case the observer
				// will clean itself up after finding the element)
				return () => observer.disconnect();
			},
		},
		{
			id: 'dashboard-redirect',
			type: 'VIRTUAL_CLICK',
			endPosition: lazyFindElement(
				{ role: 'button', name: 'Dashboard' },
				{ x: 800, y: 300 }
			),
			tooltipText:
				"Looks like you're integrated! Let's head back to the dashboard to continue.",
			blocking: true,
		},
	];
};

const createDashboardSteps = () => [
	{
		id: 'dashboard-introduction',
		type: 'DIALOGUE',
		text: "Now I'll show you how Knowtex can make medical transcription a breeze. Just follow the cursor to see the main features.",
		blocking: true,
	},
	{
		id: 'dashboard-introduction-2',
		type: 'DIALOGUE',
		text: 'Knowtex automatically syncs your patients every 45 minutes! However, you can still manually add patients. Let me show you how.',
		blocking: true,
	},
	{
		id: 'add-patient-section',
		type: 'VIRTUAL_CLICK',
		endPosition: lazyFindElement({ role: 'form' }, { x: 800, y: 500 }),
		tooltipText: "Let's add a patient to your schedule.",
		advanceMode: 'auto',
		blocking: true,
	},
	{
		id: 'type-patient-name',
		type: 'VIRTUAL_TYPING',
		endPosition: lazyFindElement(
			{ attribute: { name: 'placeholder', value: 'Patient Name' } },
			{ x: 800, y: 500 }
		),
		expectedValue: 'John Doe ',
		tooltipText: "Put in the patient's name.",
		typingDelay: 100,
		blocking: true,
	},
	{
		id: 'type-patient-mrn',
		type: 'VIRTUAL_TYPING',
		endPosition: lazyFindElement(
			{ attribute: { name: 'placeholder', value: 'MRN' } },
			{ x: 800, y: 500 }
		),
		expectedValue: '1234567890',
		tooltipText: "Put in the patient's MRN.",
		typingDelay: 100,
		blocking: true,
	},
	{
		id: 'add-patient-button',
		type: 'VIRTUAL_CLICK',
		endPosition: lazyFindElement(
			{ role: 'button', name: 'Add Patient' },
			{ x: 800, y: 500 }
		),
		tooltipText: 'Now click "Add Patient".',
		blocking: true,
	},
];

const createRecordingSteps = (specialty) => {
	const examples = getSpecialtyExamples(specialty);

	return [
		{
			id: 'ready-to-record-option',
			type: 'VIRTUAL_CLICK',
			endPosition: lazyFindClosestElement(
				{ text: 'John Doe' },
				{ selector: '[data-testid="record-link"]' }
			),
			tooltipText: 'Now click "Ready to Record".',
			blocking: true,
		},
		{
			id: 'enable-permissions-button',
			type: 'DIALOGUE',
			text: "Enable your microphone. On Chrome, it's on the top left. On Safari, you may need to allow microphone access in your browser settings.",
			blocking: true,
			advanceMode: (nextAction) => {
				const buttons = document.querySelectorAll('button');
				for (const button of buttons) {
					if (
						button.textContent &&
						button.textContent.includes('Start Recording')
					) {
						return true;
					}
				}
				return false;
			},
		},
		{
			id: 'start-recording-button',
			type: 'VIRTUAL_CLICK',
			endPosition: lazyFindElement(
				{ role: 'button', name: 'Start Recording' },
				{ x: 800, y: 500 }
			),
			tooltipText: 'Now click "Start Recording" and say hi to your patient.',
			blocking: true,
		},
		{
			id: 'recording-best-practices',
			type: 'DIALOGUE',
			text: `For best results when recording, start with patient name and visit type. Say, '${examples.patientIntro}'`,
			blocking: true,
		},
		{
			id: 'physical-findings-tips',
			type: 'DIALOGUE',
			text: `You can verbalize any objective findings, such as physical examination findings, lab results, scan results, etc. and Knowtex will document these results.`,
			blocking: true,
		},
		{
			id: 'follow-up-tips',
			type: 'DIALOGUE',
			text: `Always mention follow-up plans and next steps. Say, '${examples.followUpPlans}'`,
			blocking: true,
		},
		{
			id: 'stop-recording-button',
			type: 'VIRTUAL_CLICK',
			endPosition: lazyFindElement({ text: 'Stop' }, { x: 800, y: 500 }),
			tooltipText: "When you're done, click to stop recording.",
			blocking: true,
		},
		{
			id: 'post-recording-idle',
			type: 'IDLE',
			duration: 2000,
		},
		{
			id: 'upload-audio-button',
			type: 'VIRTUAL_CLICK',
			endPosition: lazyFindElement(
				{ role: 'button', name: 'Upload Audio' },
				{ x: 800, y: 500 }
			),
			tooltipText: 'Now upload to see the magic.',
			blocking: true,
		},
		{
			id: 'mobile-app-info',
			type: 'DIALOGUE',
			text: `Knowtex also has an Android and iOS mobile app that you can use to record your notes.`,
			blocking: true,
		},
		{
			id: 'return-home-button',
			type: 'VIRTUAL_CLICK',
			endPosition: lazyFindElement(
				{ role: 'button', name: 'Return Home' },
				{ x: 800, y: 500 }
			),
			tooltipText: 'Click here once the audio is uploaded.',
			blocking: true,
		},
		{
			id: 'ai-transcription-explanation',
			type: 'DIALOGUE',
			text: 'Our AI will transcribe your conversation and generate the note automatically. When it\'s done processing it will switch from "Processing Audio" to "Review."',
			blocking: true,
			advanceMode: 'auto',
		},
		{
			id: 'ai-transcription-explanation-wait',
			type: 'DIALOGUE',
			text: "Now let's wait for the AI to finish processing the audio. This might take a few moments.",
			blocking: true,
			advanceMode: 'external',
		},
	];
};

const createNoteReviewSteps = (specialty) => {
	return [
		{
			id: 'review-button',
			type: 'VIRTUAL_CLICK',
			endPosition: lazyFindClosestElement({ text: 'John Doe' }, 'button', {
				x: 800,
				y: 500,
			}),
			tooltipText: 'Click here to see the appointment notes.',
			blocking: true,
		},
		{
			id: 'ai-note-explanation',
			type: 'DIALOGUE',
			text: "Here's your AI-generated clinical note based on your recording. Our AI has structured the information according to your hospital's template and standard medical documentation practices.",
			blocking: true,
		},
		{
			id: 'note-textarea',
			type: 'VIRTUAL_CLICK',
			endPosition: lazyFindElement('.modal.show textarea'),
			tooltipText:
				'When you make changes, we learn more about you so your notes get better. Try clicking and editing the note.',
			advanceMode: 'external',
			tooltipAnchor: 'cursor',
			blocking: true,
			shouldScroll: true
		},
	];
};

const createIcd10CodesSteps = () => [
	{
		id: 'codes-section',
		type: 'VIRTUAL_CLICK',
		endPosition: lazyFindElement('#icd10-codes-tab', { x: 800, y: 500 }),
		tooltipText:
			'Knowtex automatically suggests relevant ICD-10 codes based on your notes.',
		advanceMode: 'auto',
		blocking: true,
	},
	{
		id: 'add-icd10-code',
		type: 'VIRTUAL_CLICK',
		endPosition: lazyFindElement('#add-icd10-code-button', { x: 800, y: 500 }),
		tooltipText: 'You can add additional codes by clicking here.',
		blocking: true,
	},
	{
		id: 'search-icd10-code',
		type: 'VIRTUAL_TYPING',
		endPosition: lazyFindElement(
			{ attribute: { name: 'placeholder', value: 'Type search...' } },
			{ x: 800, y: 500 }
		),
		expectedValue: 'hypertension',
		tooltipText: 'Search for a specific code by typing a condition.',
		typingDelay: 100,
		blocking: true,
	},
	{
		id: 'select-code-from-results',
		type: 'VIRTUAL_CLICK',
		endPosition: lazyFindElement(
			{ attribute: { name: 'data-testid', value: 'icd-search-result' } },
			{ x: 800, y: 550 }
		),
		tooltipText: 'Now select a code from the search results.',
		blocking: true,
	},
	{
		id: 'add-code-button',
		type: 'VIRTUAL_CLICK',
		endPosition: lazyFindElement('#add-code-confirm-button', {
			x: 800,
			y: 500,
		}),
		tooltipText: 'Click to add this code to your note.',
		blocking: true,
	},
];

const createEmCodingSteps = () => {
	return [
		{
			type: 'GATE_IF',
			condition: async () => {
				const emCodingHeader = await lazyFindChildElement('.modal.show', { text: 'E&M Codes' }).resolve();
				const emCodingEnabled = typeof emCodingHeader !== 'undefined' && emCodingHeader instanceof HTMLElement;
				return !!emCodingEnabled;
			},
			trueActions: [
				{
					id: 'select-level-5',
					type: 'VIRTUAL_CLICK',
					endPosition: lazyFindElement(
						{ role: 'button', name: '5' },
						{ x: 800, y: 500 }
					),
					tooltipText:
						"For this example, let's select Level 5, which is typically used for highly complex visits.",
					blocking: true,
					shouldScroll: true,
				},
				{
					id: 'view-suggested-level',
					type: 'VIRTUAL_CLICK',
					endPosition: lazyFindParentElement({ text: 'Recommended Level' }, 1, {
						x: 800,
						y: 500,
					}),
					tooltipText:
						'Knowtex automatically suggests an appropriate E&M level based on medical decision making complexity.',
					advanceMode: 'auto',
					shouldScroll: true,
					blocking: true,
				},
				{
					id: 'view-final-selection',
					type: 'VIRTUAL_CLICK',
					endPosition: lazyFindElement('#level-4-final-selection'),
					tooltipText:
						'You can select a different level if you disagree with the suggested level.',
					blocking: true,
					shouldScroll: true
				},
				{
					id: 'add-rationale',
					type: 'VIRTUAL_TYPING',
					endPosition: lazyFindElement('#rationale-textarea'),
					expectedValue:
						'This patient has multiple chronic conditions requiring complex decision making.',
					tooltipText:
						'When overriding the suggested level, you should document your rationale.',
					tooltipPosition: 'top',
					typingDelay: 50,
					blocking: true,
					shouldScroll: true
				},	
			],
			falseActions: [],
		},
		
	];
};

const createNoteFinalizationSteps = (specialty) => {
	if(!isCustomSpecialty(specialty)) {
		return 		[{
			id: 'save-changes',
			type: 'VIRTUAL_CLICK',
			endPosition: lazyFindChildElement(
				{ selector: '.modal.show' },
				{ text: 'Save Changes' }
			),
			tooltipText:
				"Let's save our changes",
			blocking: true,
			shouldScroll: true
		},
		]
	}

	return [
		{
			id: 'upload-to-emr-button',
			type: 'VIRTUAL_CLICK',
			endPosition: lazyFindChildElement(
				{ selector: '.modal.show' },
				{ text: 'Upload to EMR' }
			),
			// endPosition: lazyFindChildElement({ role: "button", name: "Upload to EMR" }, {x: 800, y: 500}),
			tooltipText:
				"If you've set up your integration, click here to upload your note directly to your EMR system.",
			shouldScroll: true,
			blocking: true,
		},
		{
			id: 'locked-note-status',
			type: 'VIRTUAL_CLICK',
			endPosition: lazyFindElement(
				{ role: 'button', name: 'Locked (View Only)' },
				{ x: 800, y: 500 }
			),
			tooltipText:
				"When you upload a note to your EHR, it becomes locked and you won't be able to edit it from inside Knowtex anymore.",
			advanceMode: 'auto',
			blocking: true,
		},
	];
};

const createConclusionSteps = () => [
	{
		id: 'calendar',
		type: 'VIRTUAL_CLICK',
		endPosition: lazyFindElement(
			{ attribute: { name: 'data-testid', value: 'datepicker' } },
			{ x: 800, y: 500 }
		),
		tooltipText:
			'You can use the calendar to view your appointments for any day. Try changing the date.',
		advanceMode: 'external',
	},
	{
		id: 'congratulations-message',
		type: 'CURSOR_TAKEOVER',
		isRedirected: false,
		messages: [
			"Congratulations! You've completed the Knowtex walkthrough.",
			"You're now ready to start using Knowtex to streamline your clinical documentation.",
			'If you have any questions, our support team is always here to help.',
		],
		cursorColor: '#5B36AF',
		blocking: true,
	},
	{
		id: 'contact-us-button',
		type: 'VIRTUAL_CLICK',
		endPosition: lazyFindElement(
			{ role: 'button', name: 'Contact Us' },
			{ x: 800, y: 500 }
		),
		tooltipText: 'Just click here to get in touch with customer support.',
		blocking: true,
	},
	{
		id: 'additional-recording-confirmation',
		type: 'DIALOGUE',
		text: 'We hope you enjoy using Knowtex to streamline your clinical documentation workflow!',
		advanceMode: 'auto',
		blocking: true,
	},
];

// Create a function that returns specialty-specific steps
export const createWalkthroughSteps = (specialty = 'self_serve') => {
	if (!isCustomSpecialty(specialty)) {
		return [];
	}

	// Get all step collections using the shared functions
	const introductionSteps = createIntroductionSteps(specialty);
	const integrationSetupSteps = createIntegrationSetupSteps(specialty);
	const dashboardSteps = createDashboardSteps();
	const recordingSteps = createRecordingSteps(specialty);
	const noteReviewSteps = createNoteReviewSteps(specialty);
	// const icd10CodesSteps = createIcd10CodesSteps();
	const emCodingSteps = createEmCodingSteps();
	const noteFinalizationSteps = createNoteFinalizationSteps(specialty);
	const conclusionSteps = createConclusionSteps();

	return [
		...introductionSteps,
		...integrationSetupSteps,
		...dashboardSteps,
		...recordingSteps,
		...noteReviewSteps,
		...emCodingSteps,
		...noteFinalizationSteps,
		...conclusionSteps,
	];
};

// New function to fetch doctor's specialty
export const getDoctorSpecialty = async () => {
	if (!localStorage.token) {
		return null;
	}
	try {
		// Get current date to use as pickedDate
		const today = new Date();
		const formattedDate = today.toISOString().split('T')[0]; // Format as YYYY-MM-DD

		// Fetch from dashboard endpoint instead of auth endpoint
		const response = await fetch(
			`${process.env.REACT_APP_API_URL}/dashboard/`,
			{
				method: 'GET',
				headers: {
					token: localStorage.token,
					pickedDate: formattedDate, // Use formatted date as pickedDate
				},
			}
		);

		const parseRes = await response.json();

		// Check if we got a valid response with specialty info
		if (parseRes && parseRes.length > 0 && parseRes[0].specialty) {
			return parseRes[0].specialty;
		}

		return null;
	} catch (err) {
		console.error('Error fetching doctor specialty:', err);
		return null;
	}
};

export const TutorialSelector = () => {
	const { clearQueue, queue, currentAction, replaceActions } = useActions();
	const { createTriggerLog } = useTriggers();
	const [specialty, setSpecialty] = useState(null);
	const {activateOnboardingSteps, setProgressBar} = useOnboarding()

	// Get specialty from API on component mount
	useEffect(() => {
		const fetchSpecialty = async () => {
			const doctorSpecialty = await getDoctorSpecialty();
			setSpecialty(doctorSpecialty);
		};

		fetchSpecialty();
	}, []);

	

	// Streamlined tutorial
	const streamlinedTutorial = () => {
		const allSteps = createFullWalkthroughSteps(specialty);
		replaceActions(allSteps);
	};

	const helpUsHelpYou = () => {
		const preSurveySteps = createPreSurveySteps(() => {});
            setProgressBar({
              totalValue: preSurveySteps.length,
              currentValue: 0, 
              label: ''
            })
            activateOnboardingSteps(preSurveySteps);
	};

	const isTutorialRunning = queue.length > 0 || currentAction;

	// If a tutorial is running, show simplified controls
	if (isTutorialRunning) {
		return (
			<div className='p-6 max-w-md mx-auto'>
				<div className='space-y-4'>
					<button
						onClick={() => {
							clearQueue();
							createTriggerLog('e6711875-f906-4c25-a3f6-47e411d4d212', true, {
								specialty: specialty,
								currentStep: currentAction?.id,
							});
						}}
						className='w-full px-4 py-3 bg-white/50 border border-accent2 text-accent1 rounded-lg hover:bg-transparent-purple text-left flex items-center'>
						<span className='mr-2'>❌</span> Skip Tutorial
					</button>
				</div>

				<div className='mt-6 pt-4 border-t text-sm text-light-grey'>
					You can exit the tutorial at any time and return to it later.
				</div>
			</div>
		);
	}

	// Otherwise show the full tutorial selector
	return (
		<div className='tutorial-selector bg-white/30 shadow-lg p-6 max-w-md mx-auto min-w-80'>
			<div className='space-y-4'>
				<button
					onClick={helpUsHelpYou}
					className='w-full px-4 py-3 bg-white/50 border border-accent2 text-accent1 rounded-lg hover:bg-transparent-purple text-left flex items-center'>
					<span className='mr-2'>🎓</span> Help us help you!
				</button>
				<button
					onClick={streamlinedTutorial}
					className='w-full px-4 py-3 bg-white/50 border border-accent2 text-accent1 rounded-lg hover:bg-transparent-purple text-left flex items-center'>
					<span className='mr-2'>🎓</span> Start Tutorial
				</button>
			</div>
		</div>
	);
};
